import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SEARCH_RESULT_LIMIT, clampSearchIndex, cycleIndex, displayName, searchDrivers } from './pickupTrackingLogic';
import { STOP_STATUS_COLOR, vehicleThemeOf } from './pickupTrackingTheme';
import type { TrackingDriver, TrackingSearchHit, TrackingSearchScope } from './pickupTrackingTypes';
import styles from './PickupDriverTrackingPage.module.css';

const SCOPES: TrackingSearchScope[] = ['driver', 'site', 'addr'];

interface TrackingSearchBoxProps {
    drivers: TrackingDriver[];
    /** 下拉是否展开由页面统一控制（点空白处 / 打开别的菜单时要一起收起） */
    open: boolean;
    onOpenChange: (open: boolean) => void;
    onPick: (hit: TrackingSearchHit, scope: TrackingSearchScope) => void;
}

export const TrackingSearchBox: React.FC<TrackingSearchBoxProps> = ({ drivers, open, onOpenChange, onPick }) => {
    const { t } = useTranslation();
    const [scope, setScope] = useState<TrackingSearchScope>('driver');
    const [query, setQuery] = useState('');
    const [idx, setIdx] = useState(0);

    const found = useMemo(() => searchDrivers(drivers, scope, query), [drivers, scope, query]);
    const shown = found.slice(0, SEARCH_RESULT_LIMIT);
    const active = clampSearchIndex(idx, found.length);
    const showResults = open && query.trim() !== '';

    const pick = (hit: TrackingSearchHit) => onPick(hit, scope);

    const footBase = t(scope === 'driver' ? 'pickup.driverTracking.footDriver' : 'pickup.driverTracking.footTrack');
    const count =
        found.length > SEARCH_RESULT_LIMIT
            ? t('pickup.driverTracking.searchCountTop', { n: found.length, top: SEARCH_RESULT_LIMIT })
            : t('pickup.driverTracking.searchCount', { n: found.length });

    const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        const n = shown.length;
        if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
            e.preventDefault();
            if (n) {
                setIdx(cycleIndex(active, n, e.key === 'ArrowDown' ? 1 : -1));
                onOpenChange(true);
            }
        } else if (e.key === 'Enter') {
            e.preventDefault();
            if (n) {
                pick(shown[active]);
                e.currentTarget.blur();
            }
        } else if (e.key === 'Escape') {
            onOpenChange(false);
        }
    };

    return (
        <div className={`${styles.sbox} ${showResults ? styles.sboxOpen : ''}`} onClick={(e) => e.stopPropagation()}>
            <div className={styles.sscope} role="tablist" aria-label={t('pickup.driverTracking.searchBy') || ''}>
                {SCOPES.map((k) => (
                    <button
                        key={k}
                        type="button"
                        role="tab"
                        aria-selected={scope === k}
                        className={`${styles.sc} ${scope === k ? styles.scOn : ''}`}
                        onClick={(e) => {
                            e.stopPropagation();
                            setScope(k);
                            setIdx(0);
                            onOpenChange(true);
                        }}
                    >
                        {t(`pickup.driverTracking.scope.${k}`)}
                    </button>
                ))}
            </div>
            <label className={styles.sinput}>
                <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.2"
                    strokeLinecap="round"
                    aria-hidden="true"
                >
                    <circle cx="11" cy="11" r="7" />
                    <path d="M20 20l-4-4" />
                </svg>
                <input
                    type="search"
                    value={query}
                    placeholder={t(`pickup.driverTracking.placeholder.${scope}`) || ''}
                    aria-label={t(`pickup.driverTracking.placeholder.${scope}`) || ''}
                    autoComplete="off"
                    onChange={(e) => {
                        setQuery(e.target.value);
                        setIdx(0);
                        onOpenChange(true);
                    }}
                    onFocus={() => onOpenChange(true)}
                    onKeyDown={onKeyDown}
                />
                {query !== '' && (
                    <button
                        type="button"
                        className={styles.sclear}
                        aria-label={t('pickup.driverTracking.clearSearch') || ''}
                        onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            setQuery('');
                            setIdx(0);
                            onOpenChange(false);
                        }}
                    >
                        ✕
                    </button>
                )}
            </label>
            {showResults && (
                <div className={styles.sres} role="listbox">
                    {shown.map((hit, i) => {
                        const d = hit.driver;
                        const s = hit.stopIdx === null ? null : d.stops[hit.stopIdx];
                        const name = displayName(d);
                        const title = s ? (scope === 'site' ? s.siteName : s.address) : name;
                        const sub = s
                            ? scope === 'site'
                                ? `${s.siteId} · ${s.address}`
                                : `${s.siteName} · ${s.siteId}`
                            : [
                                  d.id,
                                  d.vehicleType ? t(`pickup.driverTracking.vehicle.${d.vehicleType}`) : '',
                                  d.anomalies.length
                                      ? d.anomalies.map((a) => t(`pickup.driverTracking.anomaly.${a}`)).join(' / ')
                                      : '',
                              ]
                                  .filter(Boolean)
                                  .join(' · ');
                        const right = s ? `${name} · ${t(`pickup.driverTracking.status.${s.status}`)}` : `${d.ok} / ${d.total}`;
                        return (
                            <button
                                key={`${d.key}-${hit.stopIdx ?? 'd'}`}
                                type="button"
                                role="option"
                                aria-selected={i === active}
                                className={`${styles.sr} ${i === active ? styles.srOn : ''}`}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    pick(hit);
                                }}
                                onMouseEnter={() => setIdx(i)}
                            >
                                <span
                                    className={styles.srDot}
                                    style={{
                                        background: s ? STOP_STATUS_COLOR[s.status] : vehicleThemeOf(d.vehicleType).color,
                                    }}
                                />
                                <span className={styles.srM}>
                                    <b>{title}</b>
                                    <em>{sub}</em>
                                </span>
                                <span className={styles.srR}>
                                    <span>{right}</span>
                                    <em>{t(s ? 'pickup.driverTracking.actTrack' : 'pickup.driverTracking.actLocate')}</em>
                                </span>
                            </button>
                        );
                    })}
                    {found.length === 0 && <div className={styles.srEmpty}>{t('pickup.driverTracking.noResult')}</div>}
                    <div className={styles.srFoot}>{found.length ? `${count}  ·  ${footBase}` : footBase}</div>
                </div>
            )}
        </div>
    );
};
